import { useRef, useMemo, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
// Types
import RefineStopReasons from 'app/types/RefineStopReasons';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectConversation } from 'app/store/AIChat/AIChat.selectors';
// Mui
import { IconButton, Button, Tooltip, Popover, MenuList, MenuItem, Divider } from '@mui/material';
// Icons
import {
  ArrowDropDown as ArrowDropDownIcon,
  CachedOutlined as CachedOutlinedIcon
} from '@mui/icons-material';
// Context
import { IMessage } from 'app/models/ChatAI';
import { refineAnswer } from 'app/store/AIChat/AIChat.async';

type Props = {
  message: IMessage;
  lastMessage?: IMessage;
  analyzedPagesPercent: number;
}

export default function RefineMessageRefineButton({ message, lastMessage, analyzedPagesPercent }: Props) {
  const coverageOptions = [10, 25, 50, 100];

  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const conversation = useAppSelector(selectConversation);

  const anchorRef = useRef<HTMLButtonElement>(null);

  const [ coverage, setCoverage ] = useState<number | null>(null);
  const [ open, setOpen ] = useState(false);

  const maxTokenReached = useMemo(() => {
    return lastMessage?.metadata?.refineStopReason === RefineStopReasons.MaxTokens;
  }, [lastMessage]);

  const handleClick = () => {
    if ( !coverage || !conversation ) return;
    dispatch(refineAnswer({
      conversationId: conversation.id as string,
      data: { coverage: percentageToCoverage(coverage) }
    }));
  }

  const handleMenuItemClick = (newCoverage: number) => () => {
    setCoverage(newCoverage === coverage ? null : newCoverage);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const roundedAnalyzedPercents = Math.round(analyzedPagesPercent);
  const isRefineButtonDisabled = Boolean(maxTokenReached) || !coverage;
  const isCoverageButtonDisabled = Boolean(maxTokenReached) || roundedAnalyzedPercents >= 100;

  return (
    <Fragment>
      <Divider orientation="vertical" flexItem />
      <Tooltip title={maxTokenReached
        ? t('pages.aiChat.tooltip.maxTokenReached')
        : t('pages.aiChat.tooltip.refine')
      }>
        <span>
          <IconButton
            disabled={isRefineButtonDisabled}
            onClick={handleClick}
            size="small"
          ><CachedOutlinedIcon fontSize="small" /></IconButton>
        </span>
      </Tooltip>
      <Button
        ref={anchorRef}
        sx={{
          textTransform: 'none'
        }}
        disabled={isCoverageButtonDisabled}
        onClick={handleToggle}
        size="small"
        color={coverage ? 'primary' : 'inherit'}
      >
        {coverage ? `Coverage ${coverage}%` : 'Coverage'}
        <ArrowDropDownIcon fontSize="small" />
      </Button>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuList>
          {coverageOptions.map((option) => (
            <MenuItem
              key={option}
              selected={option === coverage}
              disabled={option <= roundedAnalyzedPercents}
              onClick={handleMenuItemClick(option)}
            >{`${option}%`}</MenuItem>
          ))}
        </MenuList>
      </Popover>
    </Fragment>
  )
}

const percentageToCoverage = (percentage: number) => {
  return percentage / 100;
};
